// frontend/src/modules/folha/pages/DarfPage.js
import React, { useState, useEffect } from 'react';
import { 
  FaFileInvoiceDollar, FaDownload, FaSync, FaTimes, 
  FaBarcode, FaEnvelope, FaQrcode, FaHistory, FaCheck, 
  FaClock, FaExclamationTriangle, FaCopy 
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

function DarfPage({ usuario, isDark }) {
  const [empresa, setEmpresa] = useState(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [darf, setDarf] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPix, setShowPix] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('gerar');
  const [copiaColaCopiado, setCopiaColaCopiado] = useState(false);

  const token = localStorage.getItem('token');
  const empresaId = usuario?.empresa_id || 1;

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // ============================================
  // BUSCAR EMPRESA
  // ============================================
  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const response = await fetch(`${API_URL}/api/empresas/${empresaId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setEmpresa(data);
          setEmailDestino(data.email || '');
        }
      } catch (err) {
        console.error('Erro ao buscar empresa:', err);
      }
    };
    fetchEmpresa();
  }, [empresaId, token]);

  // ============================================
  // BUSCAR HISTÓRICO
  // ============================================
  useEffect(() => {
    if (abaAtiva === 'historico') {
      buscarHistorico();
    }
  }, [abaAtiva]);

  const buscarHistorico = async () => {
    setLoadingHistorico(true);
    try {
      const response = await fetch(`${API_URL}/api/folha/darf/historico/${empresaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setHistorico(data.darfs);
      }
    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
    } finally {
      setLoadingHistorico(false);
    }
  };

  // ============================================
  // GERAR DARF
  // ============================================
  const gerarDarf = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setDarf(null);

    try {
      const response = await fetch(`${API_URL}/api/folha/darf/gerar/${empresaId}/${mes}/${ano}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao gerar DARF');
      }

      setDarf(data.darf);
      setSuccess('✅ DARF gerado com sucesso!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BAIXAR PDF
  // ============================================
  const baixarPDF = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/folha/darf/${empresaId}/${mes}/${ano}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Erro ao gerar PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DARF-${String(mes).padStart(2, '0')}-${ano}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('✅ PDF baixado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ENVIAR EMAIL
  // ============================================
  const enviarEmail = async () => {
    if (!emailDestino || !emailDestino.includes('@')) {
      setError('Email inválido');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/folha/darf/${darf.id}/enviar-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: emailDestino })
        }
      );

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error || 'Erro ao enviar email');

      setSuccess(`✅ Email enviado para ${emailDestino}`);
      setShowEmail(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // COPIAR PIX
  // ============================================
  const copiarPix = () => {
    if (darf?.pix?.copiaCola) {
      navigator.clipboard.writeText(darf.pix.copiaCola);
      setCopiaColaCopiado(true);
      setTimeout(() => setCopiaColaCopiado(false), 2000);
    }
  };

  // ============================================
  // FORMATAR MOEDA
  // ============================================
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  // ============================================
  // ESTILOS
  // ============================================
  const styles = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: '20px', color: isDark ? '#e9ecef' : '#212529' },
    header: { marginBottom: '30px' },
    title: { fontSize: '24px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
    tabs: { display: 'flex', gap: '5px', marginBottom: '20px', borderBottom: `2px solid ${isDark ? '#333' : '#e9ecef'}` },
    tab: { padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: isDark ? '#adb5bd' : '#6c757d', borderBottom: '3px solid transparent', marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '8px' },
    tabActive: { color: '#007bff', borderBottomColor: '#007bff' },
    filtros: { backgroundColor: isDark ? '#1e1e1e' : 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: '500', color: isDark ? '#adb5bd' : '#6c757d' },
    select: { padding: '8px 12px', borderRadius: '4px', border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: '14px', backgroundColor: isDark ? '#2d2d2d' : 'white', color: isDark ? '#e9ecef' : '#212529', minWidth: '150px' },
    input: { padding: '10px 12px', borderRadius: '4px', border: `1px solid ${isDark ? '#333' : '#ddd'}`, fontSize: '14px', backgroundColor: isDark ? '#2d2d2d' : 'white', color: isDark ? '#e9ecef' : '#212529', width: '100%' },
    buttonPrimary: { padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
    buttonSuccess: { padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
    buttonWarning: { padding: '10px 20px', backgroundColor: '#ffc107', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
    buttonDanger: { padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
    card: { backgroundColor: isDark ? '#1e1e1e' : 'white', padding: '25px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: `2px solid ${isDark ? '#333' : '#f0f0f0'}` },
    infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' },
    infoItem: { display: 'flex', flexDirection: 'column', gap: '5px' },
    infoLabel: { fontSize: '12px', color: isDark ? '#adb5bd' : '#6c757d', fontWeight: '500' },
    infoValue: { fontSize: '16px', fontWeight: '600', color: isDark ? '#e9ecef' : '#212529' },
    totalCard: { background: 'linear-gradient(135deg, #003366 0%, #00509e 100%)', color: 'white', padding: '30px', borderRadius: '8px', textAlign: 'center', marginBottom: '20px' },
    totalValor: { fontSize: '42px', fontWeight: 'bold', margin: '10px 0' },
    tabela: { width: '100%', borderCollapse: 'collapse', marginTop: '15px' },
    th: { padding: '12px', textAlign: 'left', borderBottom: `2px solid ${isDark ? '#333' : '#dee2e6'}`, fontSize: '13px', color: isDark ? '#adb5bd' : '#6c757d', fontWeight: '600' },
    td: { padding: '12px', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, fontSize: '14px' },
    error: { backgroundColor: '#ffebee', color: '#c62828', padding: '12px 15px', borderRadius: '4px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    success: { backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '12px 15px', borderRadius: '4px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgePendente: { backgroundColor: '#fff3cd', color: '#856404' },
    badgePago: { backgroundColor: '#d4edda', color: '#155724' },
    badgeVencido: { backgroundColor: '#f8d7da', color: '#721c24' },
    codigoBarras: { maxWidth: '100%', height: 'auto', margin: '15px 0', backgroundColor: 'white', padding: '15px', borderRadius: '4px' },
    pixQrCode: { maxWidth: '250px', margin: '15px auto', display: 'block', backgroundColor: 'white', padding: '15px', borderRadius: '8px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: isDark ? '#1e1e1e' : 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    pixCopiaCola: { display: 'flex', gap: '5px', marginTop: '15px' }
  };

  return (
    <div style={styles.container}>
      {/* Cabeçalho */}
      <div style={styles.header}>
        <h2 style={styles.title}>
          <FaFileInvoiceDollar style={{ color: '#003366' }} />
          DARF - Documento de Arrecadação
        </h2>
      </div>

      {/* Mensagens */}
      {error && (
        <div style={styles.error}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <FaTimes />
          </button>
        </div>
      )}
      {success && (
        <div style={styles.success}>
          <span>{success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* Abas */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(abaAtiva === 'gerar' ? styles.tabActive : {}) }}
          onClick={() => setAbaAtiva('gerar')}
        >
          <FaFileInvoiceDollar /> Gerar DARF
        </button>
        <button
          style={{ ...styles.tab, ...(abaAtiva === 'historico' ? styles.tabActive : {}) }}
          onClick={() => setAbaAtiva('historico')}
        >
          <FaHistory /> Histórico
        </button>
      </div>

      {/* Aba Gerar DARF */}
      {abaAtiva === 'gerar' && (
        <>
          <div style={styles.filtros}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Mês</label>
              <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))} style={styles.select}>
                {meses.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Ano</label>
              <select value={ano} onChange={(e) => setAno(parseInt(e.target.value))} style={styles.select}>
                {[2024, 2025, 2026, 2027].map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <button
              onClick={gerarDarf}
              disabled={loading}
              style={{ ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}
            >
              <FaSync className={loading ? 'spin' : ''} />
              {loading ? 'Gerando...' : 'Gerar DARF'}
            </button>
          </div>

          {darf && (
            <>
              {/* Total */}
              <div style={styles.totalCard}>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>VALOR TOTAL A RECOLHER</div>
                <div style={styles.totalValor}>{formatarMoeda(darf.valor_total)}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Vencimento: {darf.data_vencimento}</div>
              </div>

              {/* Ações */}
              <div style={{ ...styles.card, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={baixarPDF} style={styles.buttonSuccess}>
                  <FaDownload /> Baixar PDF
                </button>
                <button onClick={() => setShowEmail(true)} style={styles.buttonPrimary}>
                  <FaEnvelope /> Enviar por Email
                </button>
                <button onClick={() => setShowPix(true)} style={styles.buttonWarning}>
                  <FaQrcode /> Pagar com PIX
                </button>
              </div>

              {/* Informações */}
              <div style={styles.card}>
                <h3 style={{ marginTop: 0 }}>📋 Informações</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>CNPJ</div>
                    <div style={styles.infoValue}>{darf.cnpj}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Razão Social</div>
                    <div style={styles.infoValue}>{darf.razao_social}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Código da Receita</div>
                    <div style={styles.infoValue}>{darf.codigo_receita}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Período</div>
                    <div style={styles.infoValue}>{darf.periodo_apuracao}</div>
                  </div>
                </div>
              </div>

              {/* Código de Barras */}
              {darf.codigo_barras_img && (
                <div style={styles.card}>
                  <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaBarcode /> Código de Barras
                  </h3>
                  <img src={darf.codigo_barras_img} alt="Código de Barras" style={styles.codigoBarras} />
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', padding: '10px', backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa', borderRadius: '4px' }}>
                    {darf.codigo_barras}
                  </div>
                </div>
              )}

              {/* Detalhamento */}
              <div style={styles.card}>
                <h3 style={{ marginTop: 0 }}>📊 Detalhamento</h3>
                <table style={styles.tabela}>
                  <tbody>
                    <tr><td style={styles.td}>Base INSS</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(darf.base_inss)}</td></tr>
                    <tr><td style={styles.td}>INSS Empregados</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(darf.inss_empregados)}</td></tr>
                    <tr><td style={styles.td}>INSS Patronal</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(darf.inss_patronal)}</td></tr>
                    <tr><td style={styles.td}>RAT</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(darf.rat)}</td></tr>
                    <tr><td style={styles.td}>Terceiros</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(darf.terceiros)}</td></tr>
                    <tr style={{ fontWeight: 'bold', backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa' }}>
                      <td style={styles.td}>TOTAL</td>
                      <td style={{ ...styles.td, textAlign: 'right', color: '#003366' }}>{formatarMoeda(darf.valor_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Aba Histórico */}
      {abaAtiva === 'historico' && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>📚 Histórico de DARFs</h3>
          
          {loadingHistorico ? (
            <p style={{ textAlign: 'center', padding: '30px' }}>🔄 Carregando...</p>
          ) : historico.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '30px', color: isDark ? '#adb5bd' : '#6c757d' }}>
              📭 Nenhum DARF gerado ainda
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.tabela}>
                <thead>
                  <tr>
                    <th style={styles.th}>Período</th>
                    <th style={styles.th}>Código</th>
                    <th style={styles.th}>Vencimento</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Valor</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}><strong>{item.periodo_apuracao}</strong></td>
                      <td style={styles.td}>{item.codigo_receita}</td>
                      <td style={styles.td}>{new Date(item.data_vencimento).toLocaleDateString('pt-BR')}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{formatarMoeda(item.valor_total)}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <span style={{
                          ...styles.badge,
                          ...(item.status === 'PAGO' ? styles.badgePago : 
                              item.status === 'VENCIDO' ? styles.badgeVencido : 
                              styles.badgePendente)
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal PIX */}
      {showPix && darf?.pix && (
        <div style={styles.modal} onClick={() => setShowPix(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>💳 Pagar com PIX</h3>
              <button onClick={() => setShowPix(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                <FaTimes />
              </button>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <img src={darf.pix.qrcode} alt="QR Code PIX" style={styles.pixQrCode} />
              
              <p style={{ color: isDark ? '#adb5bd' : '#6c757d', fontSize: '14px' }}>
                Escaneie o QR Code com o app do seu banco
              </p>
              
              <div style={styles.pixCopiaCola}>
                <input
                  type="text"
                  value={darf.pix.copiaCola}
                  readOnly
                  style={{ ...styles.input, fontFamily: 'monospace', fontSize: '12px' }}
                />
                <button
                  onClick={copiarPix}
                  style={copiaColaCopiado ? styles.buttonSuccess : styles.buttonPrimary}
                >
                  {copiaColaCopiado ? <FaCheck /> : <FaCopy />}
                </button>
              </div>
              
              <p style={{ color: isDark ? '#adb5bd' : '#6c757d', fontSize: '12px', marginTop: '10px' }}>
                Valor: <strong>{formatarMoeda(darf.valor_total)}</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Email */}
      {showEmail && (
        <div style={styles.modal} onClick={() => setShowEmail(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>📧 Enviar por Email</h3>
              <button onClick={() => setShowEmail(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                <FaTimes />
              </button>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Email do destinatário</label>
              <input
                type="email"
                value={emailDestino}
                onChange={(e) => setEmailDestino(e.target.value)}
                placeholder="email@exemplo.com"
                style={styles.input}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={enviarEmail}
                disabled={loading}
                style={{ ...styles.buttonSuccess, flex: 1, justifyContent: 'center' }}
              >
                <FaEnvelope /> {loading ? 'Enviando...' : 'Enviar'}
              </button>
              <button onClick={() => setShowEmail(false)} style={{ ...styles.buttonDanger, flex: 1, justifyContent: 'center' }}>
                <FaTimes /> Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DarfPage;